/** @jsx jsx */
import React from 'react';
import FormContents from 'components/atoms/Form/FormContents';
import FlexBox from 'components/atoms/FlexBox';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import FormLabel from 'components/atoms/Form/FormLabel';
import BodyText from 'components/atoms/BodyText';
import PrimaryButton from 'components/atoms/Button/PrimaryButton';
import SecondaryButton from 'components/atoms/Button/SecondaryButton';
import { css, jsx } from '@emotion/core';
import TextForm from 'components/molecules/TextForm';
import Modal from 'components/molecules/Modal';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useApprovalAllowanceApplication } from 'components/organismos/master/attend/ApprovalApplication/hooks';
import BlockUI from 'components/molecules/BlockUi';
import { ApprovalAllowanceDetail } from 'domain/master/approval/ApprovalAllowanceApplicationDomain';
import AllowanceChangeLogModal from 'components/organismos/master/attend/AttendApplicationApproval/AllowanceChangeLogModal';

/**
 * 手当申請 承認・否決画面（C3_RESDUC-1877）
 * 手本: ApprovalModificationApplication（打刻修正申請の承認・否決画面）
 *   ・対象日の横に「更新履歴」ボタン
 *   ・否決コメントは画面上の入力欄（ダイアログでは入力しない）。ラベルに承認者を出す
 *   ・承認・否決ボタンは中央寄せ
 * 相違: 「時間」の代わりに手当の明細（手当名：申請値単位（備考））を出す
 */
const styles = {
  line: css({
    marginBottom: '15px',
  }),

  rejectInput: css`
    :not(input){
      font-weight: bold;
    }
    margin-top: 50px;
  `,
  buttonHistory: css`
    button{
      background-color: #f39c12;
      border-color: #e08e0b;
      font-size: 12px;
      padding-left: 5px;
      padding-right: 5px;
      line-height: 25px;
    }
    button:hover, button:active, button:focus{
      background-color: #f39c12;
      opacity: 0.8;
      border-color: #e08e0b;
    }
  `,
};

const ApprovalAllowanceApplication: React.FC = () => {
  const { applicationId } = useParams();

  const {
    approvalData,
    rejectReason,
    setRejectReason,
    applicationStatus,
    modalApproveOpen,
    closeApproveModal,
    modalRejectOpen,
    closeRejectModal,
    setModalApproveOpen,
    setModalRejectOpen,
    callApproveAllowanceApplication,
    callRejectAllowanceApplication,
    isLoading,
    modalHistoryOpen,
    setModalHistoryOpen,
    closeModalHistoryOpen,
    historyList,
  } = useApprovalAllowanceApplication(applicationId);

  // 更新履歴モーダルで選んでいる版（配列の添字。サーバは version 降順で返すので '0' が最新）
  const [selectTab, setSelectTab] = React.useState('0');

  const handleSubmitApproveModal = () => {
    callApproveAllowanceApplication();
    closeApproveModal();
  };
  const handleSubmitRejectModal = () => {
    callRejectAllowanceApplication();
    closeRejectModal();
  };
  const handleModalHistoryOpen = () => {
    setSelectTab('0');
    setModalHistoryOpen(true);
  };

  return (
    <BlockUI blocking={isLoading}>
      <FormContents>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="申請者" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>
              {`${approvalData.applicationStaffCode || ''} ${approvalData.applicationStaffName || ''}`}
            </BodyText>
          </FlexBoxItem>
        </FlexBox>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="申請日時" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>
              {approvalData.applicationDate != null
                && (moment(approvalData.applicationDate).isValid()
                  ? moment(approvalData.applicationDate).format('YYYY/MM/DD HH:mm:ss') : '')}
            </BodyText>
          </FlexBoxItem>
        </FlexBox>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="対象者" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>{`${approvalData.staffCode || ''} ${approvalData.staffName || ''}`}</BodyText>
          </FlexBoxItem>
        </FlexBox>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="対象日" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>
              {approvalData.targetDate != null
                && (moment(approvalData.targetDate).isValid()
                  ? moment(approvalData.targetDate).format('YYYY/MM/DD') : '')}
            </BodyText>
          </FlexBoxItem>
          <FlexBoxItem marginLeft="20px">
            <div css={styles.buttonHistory}>
              <PrimaryButton
                text="更新履歴"
                onClick={() => handleModalHistoryOpen()}
                minWidth="60%"
              />
            </div>
          </FlexBoxItem>
        </FlexBox>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="申請種別" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>
              {approvalData.modifiyStatus === 0 && '申請'}
              {approvalData.modifiyStatus === 1 && '削除'}
            </BodyText>
          </FlexBoxItem>
        </FlexBox>
        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="出勤店舗" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>{`${approvalData.orgCode || ''} ${approvalData.orgName || ''}`}</BodyText>
          </FlexBoxItem>
        </FlexBox>

        {/* 申請された手当の明細（打刻修正の「時間」に相当） */}
        {
          approvalData.detailList.map((detail: ApprovalAllowanceDetail) => (
            <FlexBox customStyle={styles.line} key={detail.allowanceId}>
              <FlexBoxItem width="200px"><FormLabel label={detail.allowanceName} /></FlexBoxItem>
              <FlexBoxItem>
                <BodyText>
                  {`${detail.allowanceValue !== null ? detail.allowanceValue : ''}${detail.dispUnit || ''}`}
                  {detail.comment ? <span style={{ marginLeft: '12px' }}>{`（${detail.comment}）`}</span> : ''}
                </BodyText>
              </FlexBoxItem>
            </FlexBox>
          ))
        }

        <FlexBox customStyle={styles.line}>
          <FlexBoxItem width="200px"><FormLabel label="申請理由" /></FlexBoxItem>
          <FlexBoxItem>
            <BodyText>{approvalData.applicationReason || ''}</BodyText>
          </FlexBoxItem>
        </FlexBox>

        {/* 否決コメント。ラベルに承認者（否決済みなら否決者）を出す。ダイアログでは入力しない */}
        <div css={styles.rejectInput}>
          <TextForm
            name="rejectReason"
            label={`${applicationStatus === 2 ? '否決者 : ' : '承認者 : '}  ${approvalData.approvalStaffCode || ''}  ${approvalData.approvalStaffName || ''}`}
            value={rejectReason || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRejectReason(e.target.value)}
            placeHolder="否決コメントを入力"
            disable={applicationStatus !== 0}
          />
        </div>

        <FlexBox customStyle={css({
          justifyContent: 'center',
          marginTop: '30px',
        })}
        >
          {applicationStatus === 0 && (
            <React.Fragment>
              <FlexBoxItem marginRight="5px">
                <PrimaryButton
                  text="承認"
                  onClick={() => setModalApproveOpen(true)}
                  minWidth="60%"
                />
              </FlexBoxItem>
              <FlexBoxItem marginLeft="5px">
                <SecondaryButton
                  text="否決"
                  onClick={() => setModalRejectOpen(true)}
                />
              </FlexBoxItem>
            </React.Fragment>
          )}
          {applicationStatus === 1 && (
            <BodyText color="#a94442" bold={true}>この申請は既に承認済みです</BodyText>
          )}
          {applicationStatus === 2 && (
            <BodyText color="#a94442" bold={true}>この申請は既に否決済みです</BodyText>
          )}
        </FlexBox>
      </FormContents>

      <Modal
        open={modalApproveOpen}
        closeHandler={closeApproveModal}
        title="承認します。よろしいですか？"
        submitText="承認"
        onSubmit={handleSubmitApproveModal}
        isShowButtonClose={true}
      />
      <Modal
        open={modalRejectOpen}
        closeHandler={closeRejectModal}
        title="否決します。よろしいですか？"
        submitText="否決"
        onSubmit={handleSubmitRejectModal}
        isShowButtonClose={true}
      />

      {/* 更新履歴（承認一覧と同じモーダルを共用） */}
      <AllowanceChangeLogModal
        open={modalHistoryOpen}
        data={historyList}
        selected={selectTab}
        setSelected={setSelectTab}
        onClose={closeModalHistoryOpen}
      />
    </BlockUI>
  );
};

export default ApprovalAllowanceApplication;
